import { usePresence } from '../../hooks/usePresence';
import { useTranslation } from 'react-i18next';

const PresenceIndicator = ({ roomId }) => {
    const activeUsers = usePresence(roomId);
    const { t } = useTranslation();

    if (!activeUsers || activeUsers.length <= 1) {
        return null; // Hide if no connection, loading, or you are the only one viewing
    }

    // Determine label Text
    const count = activeUsers.length;
    // Get other users (exclude current user theoretically, but for simplicity we can just show everyone or filtering might be needed if user is easily identifiable)
    // Let's just list everyone for transparency

    return (
        <div className="fixed bottom-24 right-4 z-50">
            <div className="relative group">
                <div className="flex items-center justify-center w-10 h-10 bg-white/10 dark:bg-slate-900/30 backdrop-blur-2xl border border-white/20 dark:border-white/10 text-slate-700 dark:text-slate-200 rounded-full shadow-[0_4px_30px_rgba(0,0,0,0.1)] transition-all duration-300 hover:bg-white/30 dark:hover:bg-slate-800/50 hover:shadow-[0_4px_30px_rgba(0,0,0,0.2)] hover:border-white/40 cursor-pointer">
                    <span className="relative flex h-2 w-2 absolute top-0.5 right-0.5">
                        <span className="animate-pulse absolute inline-flex h-full w-full rounded-full bg-blue-300 opacity-70"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-400"></span>
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px] opacity-70">
                        <path d="M4.5 6.375a4.125 4.125 0 118.25 0 4.125 4.125 0 01-8.25 0zM14.25 8.625a3.375 3.375 0 116.75 0 3.375 3.375 0 01-6.75 0zM1.5 19.125a7.125 7.125 0 0114.25 0v.003l-.001.119a.75.75 0 01-.363.63 13.067 13.067 0 01-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 01-.364-.63l-.001-.122zM17.25 19.128l-.001.144a2.25 2.25 0 01-.233.96 10.088 10.088 0 005.06-1.01.75.75 0 00.42-.643 4.875 4.875 0 00-6.957-4.611 8.586 8.586 0 011.71 5.157v.003z" />
                    </svg>
                    <span className="absolute -bottom-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full border-2 border-white dark:border-slate-900">
                        {count}
                    </span>
                </div>

                {/* Hover Tooltip/Popover */}
                <div className="absolute bottom-full right-0 mb-3 w-48 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200 transform origin-bottom-right scale-95 group-hover:scale-100">
                    <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-lg shadow-xl shadow-black/5 border border-white/50 dark:border-slate-700/50 p-3">
                        <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                            {t('presence.currentlyOnPage', 'Currently on this page')}
                        </h4>
                        <ul className="space-y-2">
                            {activeUsers.map((u) => {
                                // Extract the best possible display name
                                let displayName = 'Utilisateur inconnu';
                                let initial = '?';

                                if (u.firstName && u.lastName) {
                                    displayName = `${u.firstName} ${u.lastName}`;
                                    initial = u.firstName.charAt(0).toUpperCase();
                                } else if (u.name) {
                                    displayName = u.name;
                                    initial = u.name.charAt(0).toUpperCase();
                                } else if (u.username) {
                                    displayName = u.username;
                                    initial = u.username.charAt(0).toUpperCase();
                                } else if (u.email) {
                                    // Parse name from email as fallback (e.g. john.doe -> John Doe)
                                    const emailName = u.email.split('@')[0].replace(/\./g, ' ');
                                    displayName = emailName.replace(/\b\w/g, c => c.toUpperCase());
                                    initial = emailName.charAt(0).toUpperCase();
                                }

                                return (
                                    <li key={u._id || u.email} className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300">
                                            {initial}
                                        </div>
                                        <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                                            {displayName}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PresenceIndicator;
